import {
  Check,
  CheckCircle2,
  ChevronRight,
  CircleHelp,
  LoaderCircle,
  Send,
  Square,
  SquareCheckBig,
} from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import type {
  InteractionAnswerInput,
  InteractionOption,
  InteractionQuestion,
  InteractionQuestionType,
  InteractionRequest,
} from "../types";

interface InteractionPromptProps {
  request: InteractionRequest;
  resolution: InteractionResolution | null;
  disabled: boolean;
  onSubmit(requestId: string, answers: InteractionAnswerInput[]): Promise<boolean>;
}

export type InteractionResolution = "submitted" | "continued";

export function InteractionPrompt(props: InteractionPromptProps) {
  const [answers, setAnswers] = useState<Record<string, string[]>>(() => initialAnswers(props.request));
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const quickQuestion = props.request.questions.length === 1 && props.request.questions[0]?.type === "single"
    ? props.request.questions[0]
    : null;
  const complete = Boolean(props.resolution) || submitted;
  const valid = useMemo(
    () => props.request.questions.every((question) => answerIsValid(question, answers[question.id] ?? [])),
    [answers, props.request.questions]
  );

  async function send(nextAnswers: Record<string, string[]>) {
    if (props.disabled || submitting || complete) return;
    setSubmitting(true);
    const sent = await props.onSubmit(props.request.requestId, serializeAnswers(props.request, nextAnswers));
    if (sent) setSubmitted(true);
    setSubmitting(false);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (valid) void send(answers);
  }

  function chooseSingle(question: InteractionQuestion, optionId: string) {
    const next = { ...answers, [question.id]: [optionId] };
    setAnswers(next);
    if (quickQuestion) void send(next);
  }

  function toggleMultiple(question: InteractionQuestion, optionId: string) {
    setAnswers((current) => {
      const selected = current[question.id] ?? [];
      const next = selected.includes(optionId)
        ? selected.filter((value) => value !== optionId)
        : selected.length < question.maxSelections ? [...selected, optionId] : selected;
      return { ...current, [question.id]: next };
    });
  }

  if (complete) {
    const continued = props.resolution === "continued" && !submitted;
    const status = continued ? "Poursuivi dans le chat" : "Réponse envoyée";
    return (
      <section className={`interaction-prompt answered${continued ? " continued" : ""}`} role="status" aria-label={`${props.request.title} · ${status.toLocaleLowerCase("fr-FR")}`}>
        <span className="interaction-complete-icon"><CheckCircle2 aria-hidden="true" /></span>
        <span><strong>{props.request.title}</strong><small>{status}</small></span>
      </section>
    );
  }

  return (
    <section className={`interaction-prompt ${quickQuestion ? "quick" : "form"}`} aria-label={props.request.title}>
      <header className="interaction-heading">
        <span className="interaction-heading-icon"><CircleHelp aria-hidden="true" /></span>
        <span>
          <strong>{props.request.title}</strong>
          {props.request.description ? <small>{props.request.description}</small> : null}
        </span>
      </header>

      {quickQuestion ? (
        <div className="interaction-quick-options" role="group" aria-label={quickQuestion.label}>
          <p>{quickQuestion.label}</p>
          {quickQuestion.options.map((option) => (
            <button
              type="button"
              key={option.id}
              onClick={() => chooseSingle(quickQuestion, option.id)}
              disabled={props.disabled || submitting}
              aria-label={`${quickQuestion.label} : ${option.label}`}
            >
              <OptionCopy option={option} />
              {submitting && answers[quickQuestion.id]?.[0] === option.id
                ? <LoaderCircle className="spin" aria-hidden="true" />
                : <ChevronRight aria-hidden="true" />}
            </button>
          ))}
        </div>
      ) : (
        <form onSubmit={submit}>
          <div className="interaction-fields">
            {props.request.questions.map((question, index) => (
              <fieldset className="interaction-field" key={question.id}>
                <legend>
                  <span>{props.request.questions.length > 1 ? `${index + 1}. ` : ""}{question.label}</span>
                  {!question.required ? <small>Facultatif</small> : null}
                </legend>
                {question.description ? <p>{question.description}</p> : null}
                {question.type === "text" ? (
                  <input
                    type="text"
                    value={answers[question.id]?.[0] ?? ""}
                    onChange={(event) => setAnswers((current) => ({ ...current, [question.id]: event.target.value ? [event.target.value] : [] }))}
                    placeholder={question.placeholder || "Votre réponse"}
                    maxLength={question.maxLength}
                    required={question.required}
                    disabled={props.disabled || submitting}
                    aria-label={question.label}
                  />
                ) : (
                  <div className={`interaction-options ${question.type}`} role="group" aria-label={question.label}>
                    {question.options.map((option) => {
                      const selected = answers[question.id]?.includes(option.id) ?? false;
                      const limitReached = question.type === "multiple"
                        && !selected
                        && (answers[question.id]?.length ?? 0) >= question.maxSelections;
                      return (
                        <button
                          type="button"
                          className={selected ? "selected" : ""}
                          key={option.id}
                          onClick={() => question.type === "single"
                            ? chooseSingle(question, option.id)
                            : toggleMultiple(question, option.id)}
                          disabled={props.disabled || submitting || limitReached}
                          aria-pressed={selected}
                        >
                          <span className="interaction-option-mark">
                            {question.type === "multiple"
                              ? selected ? <SquareCheckBig aria-hidden="true" /> : <Square aria-hidden="true" />
                              : selected ? <Check aria-hidden="true" /> : <span aria-hidden="true" />}
                          </span>
                          <OptionCopy option={option} />
                        </button>
                      );
                    })}
                  </div>
                )}
              </fieldset>
            ))}
          </div>
          <footer className="interaction-submitbar">
            <button type="submit" disabled={!valid || props.disabled || submitting}>
              {submitting ? <LoaderCircle className="spin" aria-hidden="true" /> : <Send aria-hidden="true" />}
              {submitting ? "Envoi…" : props.request.submitLabel}
            </button>
          </footer>
        </form>
      )}
    </section>
  );
}

function OptionCopy({ option }: { option: InteractionOption }) {
  return (
    <span className="interaction-option-copy">
      <strong>{option.label}</strong>
      {option.description ? <small>{option.description}</small> : null}
    </span>
  );
}

export function parseInteractionRequest(value: string): InteractionRequest | null {
  try {
    const input = JSON.parse(value) as unknown;
    if (!isRecord(input)) return null;
    const requestId = readId(input.requestId);
    const title = readText(input.title, 120);
    const questions = Array.isArray(input.questions) ? input.questions.map(parseQuestion) : [];
    if (!requestId || !title || questions.length < 1 || questions.length > 5 || questions.some((question) => !question)) return null;
    const validQuestions = questions as InteractionQuestion[];
    if (new Set(validQuestions.map((question) => question.id)).size !== validQuestions.length) return null;
    return {
      requestId,
      title,
      description: readText(input.description, 500),
      submitLabel: readText(input.submitLabel, 40) || "Envoyer",
      questions: validQuestions,
    };
  } catch {
    return null;
  }
}

function parseQuestion(value: unknown): InteractionQuestion | null {
  if (!isRecord(value)) return null;
  const id = readId(value.id);
  const label = readText(value.label, 180);
  const type = questionType(value.type);
  if (!id || !label || !type) return null;
  const options = type === "text"
    ? []
    : Array.isArray(value.options) ? value.options.map(parseOption) : [];
  if (options.some((option) => !option) || (type !== "text" && (options.length < 2 || options.length > 8))) return null;
  const validOptions = options as InteractionOption[];
  if (new Set(validOptions.map((option) => option.id)).size !== validOptions.length) return null;
  const required = value.required !== false;
  const minSelections = type === "multiple" ? readInteger(value.minSelections, required ? 1 : 0) : 0;
  const maxSelections = type === "multiple" ? readInteger(value.maxSelections, validOptions.length) : type === "single" ? 1 : 0;
  const maxLength = type === "text" ? readInteger(value.maxLength, 300) : 0;
  if (type === "multiple" && (minSelections < 0 || maxSelections < 1 || minSelections > maxSelections || maxSelections > validOptions.length)) return null;
  if (type === "text" && (maxLength < 1 || maxLength > 1_000)) return null;
  return {
    id,
    label,
    description: readText(value.description, 300),
    type,
    required,
    options: validOptions,
    minSelections,
    maxSelections,
    placeholder: type === "text" ? readText(value.placeholder, 120) : "",
    maxLength,
  };
}

function parseOption(value: unknown): InteractionOption | null {
  if (!isRecord(value)) return null;
  const id = readId(value.id);
  const label = readText(value.label, 100);
  if (!id || !label) return null;
  return { id, label, description: readText(value.description, 220) };
}

function initialAnswers(request: InteractionRequest): Record<string, string[]> {
  return Object.fromEntries(request.questions.map((question) => [question.id, []]));
}

function serializeAnswers(request: InteractionRequest, answers: Record<string, string[]>): InteractionAnswerInput[] {
  return request.questions.map((question) => ({ questionId: question.id, values: answers[question.id] ?? [] }));
}

function answerIsValid(question: InteractionQuestion, values: string[]): boolean {
  if (question.type === "text" || question.type === "single") return !question.required || values.length === 1;
  return values.length >= question.minSelections && values.length <= question.maxSelections;
}

function questionType(value: unknown): InteractionQuestionType | null {
  return value === "single" || value === "multiple" || value === "text" ? value : null;
}

function readId(value: unknown): string {
  return typeof value === "string" && /^[a-z0-9][a-z0-9_-]{0,79}$/i.test(value.trim()) ? value.trim() : "";
}

function readText(value: unknown, maxLength: number): string {
  return typeof value === "string" ? value.trim().slice(0, maxLength) : "";
}

function readInteger(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) ? value : fallback;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}
